import React, { useRef } from 'react';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { FaPrint, FaReceipt } from 'react-icons/fa';
import { useQZTray } from '../../context/QZTrayContext';
import { useTranslation } from '../../context/LanguageContext';
import createItemsReportThermal from './thermal-templates/ItemsReportThermal';
import qz from 'qz-tray';

// Print-specific styles
const printStyles = `
    @media print {
        body * {
            visibility: hidden;
        }
        .items-report,
        .items-report * {
            visibility: visible;
        }
        .items-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
        }
        .no-print {
            display: none !important;
        }
        table {
            page-break-inside: auto;
        }
        tr {
            page-break-inside: avoid;
            page-break-after: auto;
        }
        thead {
            display: table-header-group;
        }
    }
`;

const ItemsReport = ({ data, language, settings }) => {
    // eslint-disable-next-line no-unused-vars
    const { t } = useTranslation();
    const { isConnected, connectQZ, printers } = useQZTray();
    // eslint-disable-next-line no-unused-vars
    const printerRef = useRef(null);

    const handlePrint = () => {
        window.print();
    };
    
    if (!data?.items) return null;

    // Ensure returns data exists
    const returns = data.returns || { total_amount: 0, count: 0, percentage: 0 };
    
    // Process return orders data if available
    const hasReturns = returns && (returns.count > 0 || returns.total_amount > 0);

    // Debug returns data

    
    // Use total_revenue from the API response's summary instead of calculating from items
    // This ensures the frontend displays the same total as what's correctly calculated in the backend
    const totalRevenue = data.summary.total_revenue;

    const handleThermalPrint = async () => {
        if (!settings || !settings.printing || !settings.printing.printer_name) {
            console.error('Printer not configured in settings');
            return;
        }

        if (!isConnected) {
            try {
                await connectQZ();
            } catch (err) {
                console.error('Failed to connect to printer:', err);
                return;
            }
        }

        // Debug returns data before thermal printing


        const thermalContent = createItemsReportThermal(data, language, settings);

        const { printing } = settings;
        const paperWidth = printing.paper_size || '80mm';
        const paperHeight = printing.paper_height || 'auto';
        // eslint-disable-next-line no-unused-vars
        const fontSize = printing.font_size || '10';

        // Calculate dimensions for printer config
        let pageWidth = parseInt(paperWidth);
        if (isNaN(pageWidth)) {
            console.error('Invalid paper width:', paperWidth);
            pageWidth = 80; // Use 80mm as default width
        }
        
        // Parse height properly - critical for preventing infinite printing
        let pageHeight;
        if (paperHeight === 'auto') {
            pageHeight = 300; // Use 300mm as default height for 'auto'

        } else {
            pageHeight = parseInt(paperHeight.replace('mm', ''));
            if (isNaN(pageHeight)) {
                console.error('Invalid paper height:', paperHeight);
                pageHeight = 300; // Use 300mm as default height
            }
        }


        // Find the matching printer
        const matchingPrinter = printers.find(printer => 
            printer.value === printing.printer_name || 
            printer.label === printing.printer_name
        );

        if (!matchingPrinter) {
            console.error(`Selected printer "${printing.printer_name}" not found in available printers:`, printers);
            return;
        }

        // Create base printer config
        const config = qz.configs.create(matchingPrinter.value);
        
        // Set essential print options - this is critical for fixing infinite printing
        config.reconfigure({
            size: { width: pageWidth, height: pageHeight },
            margins: { top: 0, right: 2, bottom: 0, left: 2 },
            colorType: 'grayscale',
            orientation: 'portrait',
            units: 'mm',
            density: '180dpi',
            scaleContent: true, // Set to true to ensure content fits properly
            bounds: {
                width: pageWidth,
                height: pageHeight
            }
        });

        // Create print data
        const printData = [{
            type: 'html',
            format: 'plain',
            data: thermalContent,
            options: {
                pageWidth: `${pageWidth}mm`,
                pageHeight: `${pageHeight}mm`,
                css: true
            }
        }];

        // Print the report
        qz.print(config, printData)
            .then(() => {
    
            })
            .catch(err => {
                console.error('[ItemsReport] Print failed:', err);
            });
    };

    return (
        <div className="items-report mt-8 space-y-8" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <style>{printStyles}</style>
            {/* Print Button */}
            <div className="flex justify-end space-x-4 no-print" style={{ direction: 'ltr' }}>
                <button
                    onClick={handleThermalPrint}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                    <FaReceipt className="mr-2" />
                    {language === 'ar' ? 'طباعة حرارية' : 'Thermal Print'}
                </button>
                <button
                    onClick={handlePrint}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <FaPrint className="mr-2" />
                    {language === 'ar' ? 'طباعة التقرير' : 'Print Report'}
                </button>
            </div>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'عدد المنتجات' : 'Total Items'}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatNumber(data.items.length)}
                    </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'الكمية المباعة' : 'Total Quantity Sold'}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatNumber(data.items.reduce((sum, item) => sum + parseInt(item.quantity_sold), 0))}
                    </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'إجمالي المبيعات' : 'Total Revenue'}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatCurrency(data.summary.total_revenue)}
                    </p>
                </div>
            </div>

            {/* Return Orders Summary (if available) */}
            {hasReturns && (
                <div className="bg-red-50 rounded-lg p-6 border border-red-100">
                    <h3 className="text-lg font-semibold mb-3 text-red-700">
                        {language === 'ar' ? 'ملخص المرتجعات' : 'Returns Summary'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'إجمالي المرتجعات' : "Total Returns Amount"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {formatCurrency(returns.total_amount || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'عدد الطلبات المرتجعة' : "Number of Returns"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {formatNumber(returns.count || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'نسبة المرتجعات' : "Returns Percentage"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {returns.percentage ? `${returns.percentage.toFixed(2)}%` : '0%'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Items Table */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {language === 'ar' ? 'أداء المنتجات' : 'Item Performance'}
                </h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'المنتج' : 'Item'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'الكمية المباعة' : 'Quantity Sold'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'إجمالي المبيعات' : 'Total Revenue'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center`}>
                                    {language === 'ar' ? 'نسبة المبيعات' : 'Revenue Share'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data.items.map((item, index) => {
                                const revenueShare = totalRevenue > 0 
                                    ? (parseFloat(item.total_revenue) / totalRevenue) * 100 
                                    : 0;

                                return (
                                    <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                            {language === 'ar' ? item.name_ar : item.name}
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                            {formatNumber(item.quantity_sold)}
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                            {formatCurrency(item.total_revenue)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="bg-indigo-600 h-2 rounded-full"
                                                        style={{ width: `${revenueShare}%` }}
                                                    />
                                                </div>
                                                <span className="ml-2">
                                                    {revenueShare.toFixed(1)}%
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ItemsReport;
